import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type ReactNode, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { SelectableCard } from '@/components/data-display/selectable-card'
import { EmptyPanel } from '@/components/feedback/empty-panel'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { DrawerFormLayout, DrawerFormRow } from '@/components/forms/drawer-form-layout'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { SearchInput } from '@/components/forms/search-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { Drawer } from '@/components/overlays/drawer'
import {
  DrawerStepProgress,
  DrawerStepTitle,
  DrawerTabs,
  DrawerWizardFooter,
} from '@/components/overlays/drawer-wizard'
import { Button } from '@/components/primitives/button'
import { runtimeConfig } from '@/config/runtime-config'
import { type ModelProviderMeta, type RegisteredModel } from '@/features/admin/model-provider/api'
import { ProviderLogo } from '@/features/admin/model-provider/provider-logo'
import { ApiError } from '@/lib/api/http-client'
import { cn } from '@/lib/cn'

import {
  createModelDistributionKey,
  getModelDistributionKeyDetail,
  updateModelDistributionKey,
  type ModelDistributionAllowedModel,
  type ModelDistributionKeyDetail,
  type ModelDistributionKeyFormValues,
  type ModelDistributionKeyQuotaRule,
  type QuotaAction,
  type QuotaMetric,
  type QuotaScope,
  type QuotaWindow,
} from './api'

type DrawerMode = 'create' | 'edit'
type CreateWizardStep = 'key' | 'models' | 'quota'
type EditDrawerTab = 'view' | 'basic' | 'models' | 'quota'
type FormErrors = Record<string, string | undefined>

interface AdminModelKeysFormDrawerProps {
  keyId?: string | null
  meta?: ModelProviderMeta
  mode: DrawerMode
  models: RegisteredModel[]
  onOpenChange: (open: boolean) => void
  onSaved?: (savedKey?: ModelDistributionKeyDetail) => void
  open: boolean
}

const createWizardSteps = ['key', 'models', 'quota'] as const
const editDrawerTabs = ['view', 'basic', 'models', 'quota'] as const
const defaultOutputProtocol = 'openai_chat_completions'

const quotaMetricValues: QuotaMetric[] = [
  'request_count',
  'prompt_tokens',
  'completion_tokens',
  'total_tokens',
  'cost_amount',
  'concurrency',
]
const quotaWindowValues: QuotaWindow[] = ['minute', 'hour', 'day', 'month', 'all_time']
const quotaScopeValues: QuotaScope[] = ['all_models', 'per_public_model', 'per_registered_model']
const quotaActionValues: QuotaAction[] = ['block', 'notify_only']

const protocolRouteByKey: Record<string, string> = {
  anthropic: '/v1/api/distribution/anthropic/messages',
  gemini: '/v1/api/distribution/gemini/models/{model}:generateContent',
  openai: '/v1/api/distribution/openai/',
  openai_chat_completions: '/v1/api/distribution/openai/',
  openai_responses: '/v1/api/distribution/openai/',
}

function createQuotaRule(): ModelDistributionKeyQuotaRule {
  return {
    action: 'block',
    currency: '',
    enabled: true,
    metric: 'request_count',
    priority: 0,
    scope: 'all_models',
    scope_value: '',
    threshold: 100,
    window: 'day',
  }
}

function defaultProtocol(meta?: ModelProviderMeta) {
  return (
    meta?.protocols.find((protocol) => protocol.key === defaultOutputProtocol)?.key ??
    meta?.protocols[0]?.key ??
    defaultOutputProtocol
  )
}

function resolveOutputProtocol(protocol: string | undefined, meta?: ModelProviderMeta) {
  if (protocol === 'openai') {
    return defaultOutputProtocol
  }

  return protocol ?? defaultProtocol(meta)
}

function initialValues(
  key: ModelDistributionKeyDetail | null | undefined,
  meta?: ModelProviderMeta,
): ModelDistributionKeyFormValues {
  if (!key) {
    return {
      allowedModelIds: [],
      name: '',
      outputProtocol: defaultProtocol(meta),
      quotaRules: [],
      status: 'active',
    }
  }

  return {
    allowedModelIds: key.allowed_models.map((model) => model.id),
    name: key.name,
    outputProtocol: resolveOutputProtocol(key.output_protocol, meta),
    quotaRules: (key.quota_rules ?? []).map((rule) => ({ ...rule })),
    status: key.status,
  }
}

function nextCreateStep(step: CreateWizardStep) {
  const index = createWizardSteps.indexOf(step)

  return createWizardSteps[Math.min(index + 1, createWizardSteps.length - 1)]
}

function previousCreateStep(step: CreateWizardStep) {
  const index = createWizardSteps.indexOf(step)

  return createWizardSteps[Math.max(index - 1, 0)]
}

function formatApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.message
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message
  }

  return fallback
}

function protocolRoute(protocol: string) {
  return protocolRouteByKey[protocol] ?? `/v1/api/distribution/${protocol}`
}

/**
 * 生成对外展示和复制的分发 endpoint。
 * 同源部署下 apiBaseUrl 是相对路径，这里补上浏览器 origin，
 * 便于直接粘贴到 Dify 等外部 OpenAI-compatible 客户端。
 */
function distributionEndpoint(protocol: string) {
  const route = protocolRoute(protocol)
  const apiBaseUrl = runtimeConfig.apiBaseUrl.trim().replace(/\/+$/, '')
  const path = apiBaseUrl.endsWith('/v1/api')
    ? `${apiBaseUrl}${route.slice('/v1/api'.length)}`
    : route

  if (path.startsWith('/')) {
    return `${window.location.origin}${path}`
  }

  return path
}

function quotaErrorKey(index: number, field: string) {
  return `quotaRules.${String(index)}.${field}`
}

function fieldElementId(errorKey: string) {
  if (errorKey === 'name') {
    return 'model-key-name'
  }

  if (errorKey === 'outputProtocol') {
    return 'model-key-outputProtocol'
  }

  if (errorKey === 'allowedModelIds') {
    return 'model-key-allowed-models'
  }

  if (errorKey === 'status') {
    return 'model-key-status'
  }

  const quotaMatch = /^quotaRules\.(\d+)\.(\w+)$/.exec(errorKey)
  if (quotaMatch) {
    return `model-key-quota-${quotaMatch[1]}-${quotaMatch[2]}`
  }

  return `model-key-${errorKey}`
}

function createStepForErrorKey(errorKey: string): CreateWizardStep {
  if (errorKey === 'allowedModelIds') {
    return 'models'
  }

  return errorKey.startsWith('quotaRules.') ? 'quota' : 'key'
}

function editTabForErrorKey(errorKey: string): EditDrawerTab {
  if (errorKey === 'allowedModelIds') {
    return 'models'
  }

  if (errorKey.startsWith('quotaRules.')) {
    return 'quota'
  }

  return 'basic'
}

function isValidThreshold(value: number | string) {
  const normalized = String(value).trim()

  if (!normalized) {
    return false
  }

  const numeric = Number(normalized)

  return Number.isFinite(numeric) && numeric >= 0
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
    </svg>
  )
}

export function AdminModelKeysFormDrawer({
  keyId,
  meta,
  mode,
  models,
  onOpenChange,
  onSaved,
  open,
}: AdminModelKeysFormDrawerProps) {
  const { t } = useTranslation('adminModelKeys')

  const detailQuery = useQuery({
    enabled: open && mode === 'edit' && Boolean(keyId),
    queryFn: () => getModelDistributionKeyDetail(keyId ?? ''),
    queryKey: ['admin-model-distribution-key-detail', keyId],
  })

  const drawerTitle =
    mode === 'create'
      ? t('drawer.createTitle')
      : t('drawer.editTitle', { name: detailQuery.data?.name ?? '' })
  const isLoading = mode === 'edit' && detailQuery.isLoading
  const isDetailError = mode === 'edit' && detailQuery.isError

  if (isLoading || isDetailError) {
    return (
      <Drawer onOpenChange={onOpenChange} open={open} size="lg" title={drawerTitle}>
        {isLoading ? (
          <div className="grid gap-4">
            <div className="h-24 animate-pulse rounded-lg bg-[var(--color-skeleton)]" />
            <div className="h-48 animate-pulse rounded-lg bg-[var(--color-skeleton)]" />
          </div>
        ) : null}
        {isDetailError ? (
          <ErrorState
            action={
              <Button onClick={() => void detailQuery.refetch()} size="sm" variant="secondary">
                {t('retry')}
              </Button>
            }
            description={formatApiError(detailQuery.error, t('drawer.detailLoadFailed'))}
            title={t('drawer.detailLoadFailedTitle')}
          />
        ) : null}
      </Drawer>
    )
  }

  return (
    <KeyedModelKeysFormContent
      key={`${mode}-${keyId ?? 'new'}-${open ? 'open' : 'closed'}-${
        detailQuery.data?.updated_at ?? 'empty'
      }-${defaultProtocol(meta)}`}
      keyId={keyId}
      meta={meta}
      mode={mode}
      modelKey={mode === 'edit' ? detailQuery.data : null}
      models={models}
      onOpenChange={onOpenChange}
      onSaved={onSaved}
      open={open}
      title={drawerTitle}
    />
  )
}

function EditDrawerTabs({
  activeTab,
  onTabChange,
}: {
  activeTab: EditDrawerTab
  onTabChange: (tab: EditDrawerTab) => void
}) {
  const { t } = useTranslation('adminModelKeys')

  return (
    <DrawerTabs
      activeTab={activeTab}
      ariaLabel={t('drawer.editTabs.label')}
      onTabChange={onTabChange}
      tabs={editDrawerTabs.map((tab) => ({
        id: tab,
        label: t(`drawer.editTabs.${tab}`),
        panelId: `model-key-edit-panel-${tab}`,
        tabId: `model-key-edit-tab-${tab}`,
      }))}
    />
  )
}

function CreateStepTitle({
  action,
  compact = false,
  step,
}: {
  action?: ReactNode
  compact?: boolean
  step: CreateWizardStep
}) {
  const { t } = useTranslation('adminModelKeys')

  return (
    <DrawerStepTitle
      action={action}
      compact={compact}
      title={t(`drawer.wizard.steps.${step}.title`)}
    />
  )
}

function KeyedModelKeysFormContent({
  keyId,
  meta,
  mode,
  modelKey,
  models,
  onOpenChange,
  onSaved,
  open,
  title,
}: {
  keyId?: string | null
  meta?: ModelProviderMeta
  mode: DrawerMode
  modelKey?: ModelDistributionKeyDetail | null
  models: RegisteredModel[]
  onOpenChange: (open: boolean) => void
  onSaved?: (savedKey?: ModelDistributionKeyDetail) => void
  open: boolean
  title: string
}) {
  const { t } = useTranslation('adminModelKeys')
  const { dismissFeedback, showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [values, setValues] = useState<ModelDistributionKeyFormValues>(() =>
    initialValues(modelKey, meta),
  )
  const [errors, setErrors] = useState<FormErrors>({})
  const [createStep, setCreateStep] = useState<CreateWizardStep>('key')
  const [editTab, setEditTab] = useState<EditDrawerTab>('view')

  const protocolOptions: SelectOption[] = useMemo(() => {
    const protocols = meta?.protocols ?? [
      { key: defaultOutputProtocol, label: 'OpenAI Chat Completions' },
    ]
    const options = protocols.map((protocol) => ({
      label: protocol.label,
      value: protocol.key,
    }))

    if (
      values.outputProtocol &&
      !options.some((option) => option.value === values.outputProtocol)
    ) {
      options.push({
        label: values.outputProtocol,
        value: values.outputProtocol,
      })
    }

    return options
  }, [meta?.protocols, values.outputProtocol])

  const allowedCallModels = useMemo(
    () =>
      values.allowedModelIds
        .map((modelId) => {
          const registeredModel = models.find((model) => model.id === modelId)
          if (registeredModel) {
            return {
              id: registeredModel.id,
              modelName: registeredModel.public_model_name,
              provider: registeredModel.provider,
              providerLabel: registeredModel.provider_label,
            }
          }

          const savedModel = modelKey?.allowed_models.find((model) => model.id === modelId)
          if (savedModel) {
            return {
              id: savedModel.id,
              modelName: savedModel.model_name,
              provider: savedModel.provider,
              providerLabel: savedModel.provider_label,
            }
          }

          return null
        })
        .filter((model): model is NonNullable<typeof model> => model !== null),
    [modelKey?.allowed_models, models, values.allowedModelIds],
  )
  const activeModelIdSet = useMemo(() => new Set(models.map((model) => model.id)), [models])

  const mutation = useMutation({
    mutationFn: () =>
      mode === 'edit'
        ? updateModelDistributionKey(keyId ?? '', values)
        : createModelDistributionKey(values),
    onError: (error) => {
      showFeedback({
        detail: formatApiError(error, t('drawer.submitFailed')),
        message: t('drawer.feedback.submitFailed'),
        scope: 'drawer',
        tone: 'error',
      })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-model-distribution-keys'] })
      void queryClient.invalidateQueries({
        queryKey: ['admin-model-distribution-key-detail', keyId],
      })
    },
    onSuccess: (savedKey) => {
      dismissFeedback('drawer')
      showFeedback({
        message:
          mode === 'create'
            ? t('drawer.feedback.createSuccess')
            : t('drawer.feedback.updateSuccess'),
        scope: 'global',
        tone: 'success',
      })
      onSaved?.(savedKey)
      onOpenChange(false)
    },
  })

  useEffect(() => {
    if (!open) {
      dismissFeedback('drawer')
    }
  }, [dismissFeedback, open])

  function updateValue<Key extends keyof ModelDistributionKeyFormValues>(
    key: Key,
    value: ModelDistributionKeyFormValues[Key],
  ) {
    dismissFeedback('drawer')
    setValues((current) => ({
      ...current,
      [key]: value,
    }))
    setErrors((current) => ({
      ...current,
      [key]: undefined,
    }))
  }

  function updateQuotaRule(index: number, patch: Partial<ModelDistributionKeyQuotaRule>) {
    dismissFeedback('drawer')
    setValues((current) => ({
      ...current,
      quotaRules: current.quotaRules.map((rule, currentIndex) =>
        currentIndex === index ? { ...rule, ...patch } : rule,
      ),
    }))
    setErrors((current) => {
      const nextErrors = { ...current }
      for (const field of Object.keys(patch)) {
        nextErrors[quotaErrorKey(index, field)] = undefined
      }
      return nextErrors
    })
  }

  function addQuotaRule() {
    updateValue('quotaRules', [...values.quotaRules, createQuotaRule()])
  }

  function removeQuotaRule(index: number) {
    updateValue(
      'quotaRules',
      values.quotaRules.filter((_, currentIndex) => currentIndex !== index),
    )
  }

  async function copyDrawerValue(value: string) {
    try {
      await navigator.clipboard.writeText(value)
      showFeedback({
        message: t('drawer.view.copySuccess'),
        scope: 'global',
        tone: 'success',
      })
    } catch {
      showFeedback({
        message: t('drawer.view.copyFailed'),
        scope: 'global',
        tone: 'error',
      })
    }
  }

  function orderedErrorKeys(scope: CreateWizardStep | 'all') {
    const keys: string[] = []

    if (scope === 'all' || scope === 'key') {
      keys.push('name', 'outputProtocol')
    }

    if (scope === 'all' || scope === 'models') {
      keys.push('allowedModelIds')
    }

    if (scope === 'all' || scope === 'quota') {
      values.quotaRules.forEach((_, index) => {
        keys.push(
          quotaErrorKey(index, 'metric'),
          quotaErrorKey(index, 'window'),
          quotaErrorKey(index, 'scope'),
          quotaErrorKey(index, 'threshold'),
          quotaErrorKey(index, 'currency'),
          quotaErrorKey(index, 'action'),
          quotaErrorKey(index, 'priority'),
        )
      })
    }

    return keys
  }

  function focusError(errorKey: string) {
    window.requestAnimationFrame(() => {
      document.getElementById(fieldElementId(errorKey))?.focus()
    })
  }

  function validateForm(scope: CreateWizardStep | 'all' = 'all') {
    const nextErrors: FormErrors = {}
    const shouldValidateKey = scope === 'all' || scope === 'key'
    const shouldValidateModels = scope === 'all' || scope === 'models'
    const shouldValidateQuota = scope === 'all' || scope === 'quota'

    if (shouldValidateKey && !values.name.trim()) {
      nextErrors.name = t('drawer.validation.nameRequired')
    }

    if (shouldValidateKey && !values.outputProtocol.trim()) {
      nextErrors.outputProtocol = t('drawer.validation.protocolRequired')
    }

    if (shouldValidateModels && values.allowedModelIds.length === 0) {
      nextErrors.allowedModelIds = t('drawer.validation.allowedModelsRequired')
    }

    if (
      shouldValidateModels &&
      values.allowedModelIds.some((modelId) => !activeModelIdSet.has(modelId))
    ) {
      nextErrors.allowedModelIds = t('drawer.validation.allowedModelsUnavailable')
    }

    if (shouldValidateQuota) {
      values.quotaRules.forEach((rule, index) => {
        if (!isValidThreshold(rule.threshold)) {
          nextErrors[quotaErrorKey(index, 'threshold')] = t(
            'drawer.validation.quotaThresholdRequired',
          )
        }
        if (rule.metric === 'cost_amount' && !(rule.currency ?? '').trim()) {
          nextErrors[quotaErrorKey(index, 'currency')] = t(
            'drawer.validation.quotaCurrencyRequired',
          )
        }
      })
    }

    setErrors(nextErrors)
    const firstErrorKey =
      orderedErrorKeys(scope).find((key) => Boolean(nextErrors[key])) ?? Object.keys(nextErrors)[0]

    if (firstErrorKey) {
      if (mode === 'create' && scope === 'all') {
        setCreateStep(createStepForErrorKey(firstErrorKey))
      }
      if (mode === 'edit' && scope === 'all') {
        setEditTab(editTabForErrorKey(firstErrorKey))
      }
      focusError(firstErrorKey)
      showFeedback({
        detail: nextErrors[firstErrorKey],
        message: t('drawer.feedback.validationFailed'),
        scope: 'drawer',
        tone: 'error',
      })
    }

    return Object.keys(nextErrors).length === 0
  }

  function submitForm() {
    if (!validateForm()) {
      return
    }

    mutation.mutate()
  }

  function handleCreatePrimaryAction() {
    if (createStep !== 'quota') {
      if (!validateForm(createStep)) {
        return
      }

      setCreateStep(nextCreateStep(createStep))
      return
    }

    submitForm()
  }

  function handleCreateSecondaryAction() {
    if (createStep === 'key') {
      onOpenChange(false)
      return
    }

    setCreateStep(previousCreateStep(createStep))
  }

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()

    if (mode === 'create') {
      handleCreatePrimaryAction()
      return
    }

    submitForm()
  }

  function renderViewPanel() {
    return (
      <div className="grid gap-5">
        <section className="grid gap-3">
          <div>
            <h3 className="text-sm font-bold text-[#374151]">{t('drawer.view.endpointTitle')}</h3>
            <p className="mt-1 text-xs text-[var(--color-text-subtle)]">
              {t('drawer.view.endpointDescription')}
            </p>
          </div>
          <CopyableReadonlyField
            copyLabel={t('drawer.view.copyEndpoint')}
            label={t('drawer.view.endpoint')}
            monospace
            onCopy={() => void copyDrawerValue(distributionEndpoint(values.outputProtocol))}
            value={distributionEndpoint(values.outputProtocol)}
          />
        </section>
        <section className="grid gap-3">
          <div>
            <h3 className="text-sm font-bold text-[#374151]">{t('drawer.view.modelsTitle')}</h3>
            <p className="mt-1 text-xs text-[var(--color-text-subtle)]">
              {t('drawer.view.modelsDescription')}
            </p>
          </div>
          <div className="grid gap-2">
            {allowedCallModels.length === 0 ? (
              <EmptyPanel className="py-6">{t('drawer.view.modelsEmpty')}</EmptyPanel>
            ) : null}
            {allowedCallModels.map((model) => (
              <CopyableReadonlyField
                copyLabel={t('drawer.view.copyModel', { name: model.modelName })}
                key={model.id}
                label={model.modelName}
                onCopy={() => void copyDrawerValue(model.modelName)}
                prefix={
                  <ProviderLogo
                    className="size-7 rounded-lg p-1.5"
                    label={model.providerLabel ?? model.provider}
                    provider={model.provider}
                    variant="compact"
                  />
                }
                value={model.modelName}
              />
            ))}
          </div>
        </section>
      </div>
    )
  }

  function renderKeyFields(includeStatus: boolean) {
    return (
      <DrawerFormLayout className="mt-0">
        {includeStatus ? (
          <DrawerFormRow columns={1}>
            <FormField htmlFor="model-key-status" label={t('drawer.fields.enabled')}>
              <div className="flex h-[38px] items-center gap-3">
                <Switch
                  aria-label={t('drawer.fields.enabled')}
                  checked={values.status === 'active'}
                  id="model-key-status"
                  onCheckedChange={(checked) =>
                    updateValue('status', checked ? 'active' : 'disabled')
                  }
                />
                <span className="text-sm text-[var(--color-text-muted)]">
                  {t(`status.${values.status}`)}
                </span>
              </div>
            </FormField>
          </DrawerFormRow>
        ) : null}
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.name}
            htmlFor="model-key-name"
            label={t('drawer.fields.name')}
            required
          >
            <Input
              id="model-key-name"
              invalid={Boolean(errors.name)}
              onChange={(event) => updateValue('name', event.target.value)}
              placeholder={t('drawer.placeholders.name')}
              value={values.name}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.outputProtocol}
            htmlFor="model-key-outputProtocol"
            label={t('drawer.fields.outputProtocol')}
            required
          >
            <Select
              aria-label={t('drawer.fields.outputProtocol')}
              id="model-key-outputProtocol"
              invalid={Boolean(errors.outputProtocol)}
              onValueChange={(value) => updateValue('outputProtocol', value)}
              options={protocolOptions}
              value={values.outputProtocol}
            />
          </FormField>
        </DrawerFormRow>
      </DrawerFormLayout>
    )
  }

  function renderModelSelection(showTitle = true) {
    return (
      <ModelSelectionBox
        error={errors.allowedModelIds}
        models={models}
        onChange={(nextIds) => updateValue('allowedModelIds', nextIds)}
        savedModels={modelKey?.allowed_models ?? []}
        selectedIds={values.allowedModelIds}
        showTitle={showTitle}
      />
    )
  }

  function renderAddQuotaRuleButton() {
    return (
      <Button onClick={addQuotaRule} size="sm" variant="secondary">
        <PlusIcon />
        {t('quota.addRule')}
      </Button>
    )
  }

  function renderQuotaRules(showTitle = true, showAction = true) {
    return (
      <QuotaRulesEditor
        errors={errors}
        onAdd={addQuotaRule}
        onRemove={removeQuotaRule}
        onRuleChange={updateQuotaRule}
        rules={values.quotaRules}
        showAction={showAction}
        showTitle={showTitle}
      />
    )
  }

  const createStepIndex = createWizardSteps.indexOf(createStep)
  const drawerTitleMeta =
    mode === 'create'
      ? t('drawer.wizard.headerDescription', {
          current: createStepIndex + 1,
          total: createWizardSteps.length,
        })
      : undefined
  const drawerHeaderAddon =
    mode === 'create' ? (
      <DrawerStepProgress
        ariaLabel={t('drawer.wizard.progressLabel', {
          current: createStepIndex + 1,
          step: t(`drawer.wizard.steps.${createStep}.title`),
          total: createWizardSteps.length,
        })}
        className="w-[220px]"
        currentStep={createStep}
        steps={createWizardSteps}
      />
    ) : (
      <EditDrawerTabs activeTab={editTab} onTabChange={setEditTab} />
    )
  const drawerFooter =
    mode === 'create' ? (
      <DrawerWizardFooter
        backLabel={t('drawer.wizard.previous')}
        canGoBack={createStep !== 'key'}
        isLoading={mutation.isPending}
        nextLabel={createStep === 'quota' ? t('drawer.wizard.create') : t('drawer.wizard.next')}
        onBack={handleCreateSecondaryAction}
        onNext={handleCreatePrimaryAction}
      />
    ) : (
      <>
        <Button
          disabled={mutation.isPending}
          onClick={() => onOpenChange(false)}
          variant="secondary"
        >
          {t('drawer.cancel')}
        </Button>
        <Button form="model-key-form" isLoading={mutation.isPending} type="submit">
          {t('drawer.save')}
        </Button>
      </>
    )

  let createStepContent: ReactNode = null

  if (createStep === 'key') {
    createStepContent = (
      <div>
        <CreateStepTitle step={createStep} />
        {renderKeyFields(false)}
      </div>
    )
  } else if (createStep === 'models') {
    createStepContent = (
      <div>
        <CreateStepTitle compact step={createStep} />
        {renderModelSelection(false)}
      </div>
    )
  } else {
    createStepContent = (
      <div>
        <CreateStepTitle action={renderAddQuotaRuleButton()} compact step={createStep} />
        {renderQuotaRules(false, false)}
      </div>
    )
  }

  return (
    <Drawer
      footer={drawerFooter}
      headerAddon={drawerHeaderAddon}
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={title}
      titleMeta={drawerTitleMeta}
    >
      <form id="model-key-form" onSubmit={handleSubmit}>
        {mode === 'create' ? (
          <div className="pb-6">{createStepContent}</div>
        ) : (
          <div
            aria-labelledby={`model-key-edit-tab-${editTab}`}
            id={`model-key-edit-panel-${editTab}`}
            role="tabpanel"
            tabIndex={0}
          >
            {editTab === 'view' ? renderViewPanel() : null}
            {editTab === 'basic' ? renderKeyFields(true) : null}
            {editTab === 'models' ? renderModelSelection() : null}
            {editTab === 'quota' ? renderQuotaRules() : null}
          </div>
        )}
      </form>
    </Drawer>
  )
}

function ModelSelectionBox({
  error,
  models,
  onChange,
  savedModels,
  selectedIds,
  showTitle = true,
}: {
  error?: string
  models: RegisteredModel[]
  onChange: (nextIds: string[]) => void
  savedModels: ModelDistributionAllowedModel[]
  selectedIds: string[]
  showTitle?: boolean
}) {
  const { t } = useTranslation('adminModelKeys')
  const [search, setSearch] = useState('')
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectionModels = useMemo(() => {
    const activeModels = models.map((model) => ({
      id: model.id,
      interfaceLabel: model.interface_label ?? model.interface_type,
      publicModelName: model.public_model_name,
      provider: model.provider,
      providerLabel: model.provider_label,
      unavailable: false,
    }))
    const activeIdSet = new Set(activeModels.map((model) => model.id))
    const unavailableSelectedModels = savedModels
      .filter((model) => selectedIdSet.has(model.id) && !activeIdSet.has(model.id))
      .map((model) => ({
        id: model.id,
        interfaceLabel: t('drawer.models.unavailable'),
        publicModelName: model.model_name,
        provider: model.provider,
        providerLabel: model.provider_label,
        unavailable: true,
      }))

    return [...activeModels, ...unavailableSelectedModels]
  }, [models, savedModels, selectedIdSet, t])
  const filteredModels = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    return selectionModels.filter(
      (model) =>
        !keyword ||
        model.publicModelName.toLowerCase().includes(keyword) ||
        model.interfaceLabel.toLowerCase().includes(keyword),
    )
  }, [search, selectionModels])

  function toggleModel(modelId: string) {
    const nextIds = selectedIdSet.has(modelId)
      ? selectedIds.filter((selectedId) => selectedId !== modelId)
      : [...selectedIds, modelId]

    onChange(nextIds)
  }

  return (
    <section className="space-y-3">
      <div className="min-w-0">
        {showTitle ? (
          <h3 className="text-sm font-bold text-[#374151]">{t('drawer.models.title')}</h3>
        ) : null}
        <p className={cn('text-xs text-[var(--color-text-subtle)]', showTitle ? 'mt-1' : '')}>
          {t('drawer.models.description')}
        </p>
      </div>
      <div
        aria-invalid={Boolean(error) || undefined}
        aria-label={t('drawer.models.title')}
        className="space-y-3"
        id="model-key-allowed-models"
        role="group"
        tabIndex={-1}
      >
        <SearchInput
          aria-label={t('drawer.models.searchLabel')}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('drawer.models.searchPlaceholder')}
          value={search}
        />
        <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
          {filteredModels.length === 0 ? (
            <EmptyPanel align="center">
              {selectionModels.length === 0
                ? t('drawer.models.empty')
                : t('drawer.models.noMatches')}
            </EmptyPanel>
          ) : null}
          {filteredModels.map((model) => {
            const checked = selectedIdSet.has(model.id)

            return (
              <SelectableCard
                as="label"
                className="flex min-h-[54px] cursor-pointer items-center gap-3 px-3 py-2.5"
                key={model.id}
                selected={checked}
                selectedClassName={
                  model.unavailable
                    ? 'border-[#f8d7a6] bg-[#fffaf0]'
                    : 'border-[var(--color-primary)] bg-[rgba(30,95,255,0.06)]'
                }
                unselectedClassName={
                  model.unavailable
                    ? 'border-[#f8d7a6] bg-[#fffaf0]'
                    : 'border-[var(--color-control-border)] hover:border-[rgba(30,95,255,0.42)]'
                }
              >
                <input
                  checked={checked}
                  className="size-4 accent-[var(--color-primary)]"
                  onChange={() => toggleModel(model.id)}
                  type="checkbox"
                />
                <ProviderLogo
                  className="size-7 rounded-lg p-1.5"
                  label={model.providerLabel ?? model.provider}
                  provider={model.provider}
                  variant="compact"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-[var(--color-text)]">
                    {model.publicModelName}
                  </span>
                </span>
                <Badge className="shrink-0" variant={model.unavailable ? 'warning' : 'neutral'}>
                  {model.interfaceLabel}
                </Badge>
              </SelectableCard>
            )
          })}
        </div>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
      </div>
    </section>
  )
}

function QuotaRulesEditor({
  errors,
  onAdd,
  onRemove,
  onRuleChange,
  rules,
  showAction = true,
  showTitle = true,
}: {
  errors: FormErrors
  onAdd: () => void
  onRemove: (index: number) => void
  onRuleChange: (index: number, patch: Partial<ModelDistributionKeyQuotaRule>) => void
  rules: ModelDistributionKeyQuotaRule[]
  showAction?: boolean
  showTitle?: boolean
}) {
  const { t } = useTranslation('adminModelKeys')
  const metricOptions = quotaMetricValues.map((value) => ({
    label: t(`quota.metrics.${value}`),
    value,
  }))
  const windowOptions = quotaWindowValues.map((value) => ({
    label: t(`quota.windows.${value}`),
    value,
  }))
  const scopeOptions = quotaScopeValues.map((value) => ({
    label: t(`quota.scopes.${value}`),
    value,
  }))
  const actionOptions = quotaActionValues.map((value) => ({
    label: t(`quota.actions.${value}`),
    value,
  }))

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {showTitle ? (
            <h3 className="text-sm font-bold text-[#374151]">{t('quota.title')}</h3>
          ) : null}
          <p className={cn('text-xs text-[var(--color-text-subtle)]', showTitle ? 'mt-1' : '')}>
            {t('quota.description')}
          </p>
        </div>
        {showAction ? (
          <Button onClick={onAdd} size="sm" variant="secondary">
            <PlusIcon />
            {t('quota.addRule')}
          </Button>
        ) : null}
      </div>
      {rules.length === 0 ? (
        <EmptyPanel>{t('quota.empty')}</EmptyPanel>
      ) : (
        <div className="space-y-4">
          {rules.map((rule, index) => {
            const fieldPrefix = `model-key-quota-${String(index)}`

            return (
              <div
                className="rounded-lg border border-[var(--color-panel-border)] bg-[#fbfbfb] p-4"
                key={`${String(rule.id ?? index)}-${rule.metric}-${rule.window}`}
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold tracking-wide text-[var(--color-text-muted)] uppercase">
                    {t('quota.ruleTitle', { index: index + 1 })}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[var(--color-text-muted)]">
                      {rule.enabled !== false ? t('quota.enabled') : t('quota.disabled')}
                    </span>
                    <Switch
                      aria-label={t('quota.enabledLabel', { index: index + 1 })}
                      checked={rule.enabled !== false}
                      onCheckedChange={(checked) => onRuleChange(index, { enabled: checked })}
                    />
                    <Button onClick={() => onRemove(index)} size="sm" variant="ghost">
                      {t('quota.removeRule')}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-x-5 gap-y-[18px] sm:grid-cols-2">
                  <FormField
                    error={errors[quotaErrorKey(index, 'metric')]}
                    htmlFor={`${fieldPrefix}-metric`}
                    label={t('quota.metric')}
                    required
                  >
                    <Select
                      aria-label={t('quota.metric')}
                      id={`${fieldPrefix}-metric`}
                      invalid={Boolean(errors[quotaErrorKey(index, 'metric')])}
                      onValueChange={(value) =>
                        onRuleChange(index, { metric: value as QuotaMetric })
                      }
                      options={metricOptions}
                      value={rule.metric}
                    />
                  </FormField>
                  <FormField
                    error={errors[quotaErrorKey(index, 'window')]}
                    htmlFor={`${fieldPrefix}-window`}
                    label={t('quota.window')}
                    required
                  >
                    <Select
                      aria-label={t('quota.window')}
                      id={`${fieldPrefix}-window`}
                      invalid={Boolean(errors[quotaErrorKey(index, 'window')])}
                      onValueChange={(value) =>
                        onRuleChange(index, { window: value as QuotaWindow })
                      }
                      options={windowOptions}
                      value={rule.window}
                    />
                  </FormField>
                  <FormField
                    error={errors[quotaErrorKey(index, 'scope')]}
                    htmlFor={`${fieldPrefix}-scope`}
                    label={t('quota.scope')}
                    required
                  >
                    <Select
                      aria-label={t('quota.scope')}
                      id={`${fieldPrefix}-scope`}
                      invalid={Boolean(errors[quotaErrorKey(index, 'scope')])}
                      onValueChange={(value) => onRuleChange(index, { scope: value as QuotaScope })}
                      options={scopeOptions}
                      value={rule.scope}
                    />
                  </FormField>
                  <FormField
                    error={errors[quotaErrorKey(index, 'threshold')]}
                    htmlFor={`${fieldPrefix}-threshold`}
                    label={t('quota.threshold')}
                    required
                  >
                    <Input
                      id={`${fieldPrefix}-threshold`}
                      invalid={Boolean(errors[quotaErrorKey(index, 'threshold')])}
                      min={0}
                      onChange={(event) => onRuleChange(index, { threshold: event.target.value })}
                      step="0.000001"
                      type="number"
                      value={String(rule.threshold)}
                    />
                  </FormField>
                  <FormField
                    error={errors[quotaErrorKey(index, 'currency')]}
                    htmlFor={`${fieldPrefix}-currency`}
                    label={t('quota.currency')}
                    required={rule.metric === 'cost_amount'}
                  >
                    <Input
                      id={`${fieldPrefix}-currency`}
                      invalid={Boolean(errors[quotaErrorKey(index, 'currency')])}
                      onChange={(event) => onRuleChange(index, { currency: event.target.value })}
                      placeholder={t('quota.currencyPlaceholder')}
                      value={rule.currency ?? ''}
                    />
                  </FormField>
                  <FormField htmlFor={`${fieldPrefix}-priority`} label={t('quota.priority')}>
                    <Input
                      id={`${fieldPrefix}-priority`}
                      onChange={(event) =>
                        onRuleChange(index, {
                          priority: Number.parseInt(event.target.value || '0', 10) || 0,
                        })
                      }
                      step="1"
                      type="number"
                      value={String(rule.priority ?? 0)}
                    />
                  </FormField>
                  <FormField htmlFor={`${fieldPrefix}-scopeValue`} label={t('quota.scopeValue')}>
                    <Input
                      id={`${fieldPrefix}-scopeValue`}
                      onChange={(event) => onRuleChange(index, { scope_value: event.target.value })}
                      placeholder={t('quota.scopeValuePlaceholder')}
                      value={rule.scope_value ?? ''}
                    />
                  </FormField>
                  <FormField
                    error={errors[quotaErrorKey(index, 'action')]}
                    htmlFor={`${fieldPrefix}-action`}
                    label={t('quota.action')}
                    required
                  >
                    <Select
                      aria-label={t('quota.action')}
                      id={`${fieldPrefix}-action`}
                      invalid={Boolean(errors[quotaErrorKey(index, 'action')])}
                      onValueChange={(value) =>
                        onRuleChange(index, { action: value as QuotaAction })
                      }
                      options={actionOptions}
                      value={rule.action}
                    />
                  </FormField>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
